import {
  NotificationPagination,
  PushNotificationRecord,
} from "../../features/auth/store/notificationSlice";

type NotificationHistoryTableProps = {
  notifications: PushNotificationRecord[];
  loading?: boolean;
};

const formatDate = (timestamp?: string) => {
  if (!timestamp) return "N/A";
  const date = new Date(timestamp);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
};

const formatTime = (timestamp?: string) => {
  if (!timestamp) return "N/A";
  const date = new Date(timestamp);
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const statusStyles: Record<string, string> = {
  completed: "text-green-500",
  queued: "text-yellow-600",
};

export const NotificationHistoryTable = ({
  notifications,
  loading = false,
}: NotificationHistoryTableProps) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!notifications || notifications.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 border border-[#FFFFFF1A] rounded-2xl">
        <div className="text-center">
          <p className="text-gray-500 dark:text-gray-400 text-lg mb-2">
            No notifications sent yet
          </p>
          <p className="text-gray-400 dark:text-gray-500 text-sm">
            Notifications you send will show up here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border border-[#FFFFFF1A] rounded-2xl overflow-hidden">
        <thead>
          <tr className="bg-gray-100 dark:bg-[#FFFFFF1A] text-gray-600 dark:text-white uppercase text-xs bp:text-sm leading-normal h-[50px]">
            <th className="px-6 text-left">Title</th>
            <th className="px-6 text-left hidden sm:table-cell">Sent</th>
            <th className="px-6 text-left">Recipients</th>
            <th className="px-6 text-left hidden sm:table-cell">
              Sent / Failed
            </th>
            <th className="px-6 text-left">Status</th>
            <th className="px-6 text-left hidden sm:table-cell">Sent By</th>
          </tr>
        </thead>
        <tbody className="text-gray-600 dark:text-white text-xs bp:text-sm">
          {notifications.map((notification) => (
            <tr
              key={notification.id}
              className="border-b border-gray-200 h-[50px] hover:bg-gray-50 dark:hover:bg-gray-50/5"
            >
              <td className="px-6">
                <div className="flex flex-col">
                  <span className="font-medium">{notification.title}</span>
                  <span className="text-[11px] text-gray-500 dark:text-gray-400 truncate max-w-[200px]">
                    {notification.body}
                  </span>
                </div>
              </td>
              <td className="px-6 hidden sm:table-cell">
                <div className="flex flex-col">
                  <span>{formatDate(notification.created_at)}</span>
                  <span className="text-[11px] text-gray-500 dark:text-gray-400">
                    {formatTime(notification.created_at)}
                  </span>
                </div>
              </td>
              <td className="px-6">{notification.recipients_count}</td>
              <td className="px-6 hidden sm:table-cell">
                {notification.sent_count} / {notification.failed_count}
              </td>
              <td className="px-6">
                <span
                  className={`px-2 py-1 rounded text-sm font-medium ${
                    statusStyles[notification.status] || "text-gray-500"
                  }`}
                >
                  {notification.status}
                </span>
              </td>
              <td className="px-6 hidden sm:table-cell">
                {notification.created_by || "-"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

type NotificationHistoryPaginationProps = {
  pagination: NotificationPagination;
  loading: boolean;
  onPageChange: (page: number) => void;
};

export const NotificationHistoryPagination = ({
  pagination,
  loading,
  onPageChange,
}: NotificationHistoryPaginationProps) => {
  const { current_page, last_page, from, to, total } = pagination;

  const handlePrevious = () => {
    if (current_page > 1) onPageChange(current_page - 1);
  };

  const handleNext = () => {
    if (current_page < last_page) onPageChange(current_page + 1);
  };

  return (
    <div className="flex flex-col md:flex-row md:justify-between md:items-center border-t border-[#FFFFFF1A] p-4 gap-2">
      <div className="flex gap-2">
        <button
          className="border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-4 py-2 rounded-md text-xs sm:text-sm dark:text-white dark:bg-[#FFFFFF1A] disabled:opacity-50 hover:bg-gray-100 dark:hover:bg-[#FFFFFF33]"
          onClick={handlePrevious}
          disabled={current_page <= 1 || loading}
        >
          Previous
        </button>
        <button
          className="border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-4 py-2 rounded-md text-xs sm:text-sm dark:text-white dark:bg-[#FFFFFF1A] disabled:opacity-50 hover:bg-gray-100 dark:hover:bg-[#FFFFFF33]"
          onClick={handleNext}
          disabled={current_page >= last_page || loading}
        >
          Next
        </button>
      </div>
      <span className="text-gray-600 dark:text-white text-xs sm:text-sm">
        Showing {from || 0} to {to || 0} of {total || 0} results
        {last_page > 1 && ` (Page ${current_page} of ${last_page})`}
      </span>
    </div>
  );
};
